<template>
    <table-container>
        <data-iterator
            ref="iterator"
            :api="api"
            :filters.sync="filters"
            @update:filters="updateFilters"
        >
            <template v-slot:filters="{ busy }">
                <table-filters
                    v-model="filters"
                    :fields="headers"
                    :busy="busy"
                    :list-filters="listFilters"
                    @input="updateFilters"
                ></table-filters>
            </template>

            <template slot-scope="{ items }">
                <d-table
                    :items="items"
                    :headers="headers"
                    :filters.sync="filters"
                    class="orders-table"
                    row-action
                    @onRowSelected="onRowSelected"
                >
                    <!-- createdAt -->
                    <template v-slot:[`item.createdAt`]="{ item }">
                        <identifier
                            :item="item"
                            :get-id="getIdFromIri"
                            :show-edit-link="isSuperAdmin || isCompanyAdmin"
                            :route-type="routeType"
                        ></identifier>
                        <warning
                            v-if="
                                isSuperAdmin &&
                                ((item.deliveryJob &&
                                    item.deliveryJob.warning) ||
                                    item.warning)
                            "
                            :item="item"
                            @execute="removeWarning(item.id)"
                        ></warning>
                    </template>

                    <!-- status -->
                    <template v-slot:[`item.status`]="{ item }">
                        <status :item="item"></status>
                    </template>

                    <!-- delivery -->
                    <template v-slot:[`item.deliveryJob`]="{ item }">
                        <delivery-status :item="item"></delivery-status>
                    </template>

                    <!-- deliveryJob.pickupTime -->
                    <template v-slot:[`item.deliveryJob.pickupTime`]="{ item }">
                        <router-link
                            :to="
                                getIdFromIri(
                                    item,
                                    'restaurant',
                                    routeType.RESTAURANT_EDIT,
                                    'id'
                                )
                            "
                        >
                            {{ item.restaurantName }}
                        </router-link>
                        <small
                            v-if="
                                item.deliveryJob &&
                                item.deliveryJob.primaryMethod !==
                                    'restaurant_managed'
                            "
                            class="d-flex flex-nowrap g-73--text"
                        >
                            {{ item.deliveryJob.pickupTime | transformDate }}
                        </small>
                    </template>

                    <!-- dropOff -->
                    <template v-slot:[`item.requiredDeliveryTime`]="{ item }">
                        <drop-off :item="item"></drop-off>
                    </template>

                    <!-- deliveryName -->
                    <template v-slot:[`item.deliveryName`]="{ item }">
                        <router-link
                            v-if="item.userProfile"
                            :to="
                                getIdFromIri(
                                    item.userProfile,
                                    '@id',
                                    routeType.USER_EDIT,
                                    'userId'
                                )
                            "
                        >
                            {{ item.userProfile | toFullName }}
                        </router-link>
                        <template v-else> {{ item.deliveryName }} </template>
                        <small
                            v-if="
                                item.companyName &&
                                item.companyName !== item.deliveryName
                            "
                            class="d-flex g-73--text"
                        >
                            {{ item.companyName }}
                        </small>
                    </template>

                    <!-- priceTotal -->
                    <template v-slot:[`item.priceTotal`]="{ item }">
                        {{
                            item.priceTotal
                                | currency(item.currency, item.currencyLocale)
                        }}
                        <small class="d-flex g-73--text">
                            {{
                                $t(
                                    'enums.payment_methods.' +
                                        (item.paymentMethod || 'company')
                                )
                            }}
                        </small>
                    </template>

                    <!-- sent -->
                    <template v-slot:[`item.sent`]="{ item }">
                        <flag
                            :active="parseFloat(item.sentRatio || 0) >= 1"
                        ></flag>
                    </template>

                    <!-- cost code -->
                    <template v-slot:[`item.costCode`]="{ item }">
                        <edit-cost-code
                            :item="item"
                            :api="api"
                            :update-list="updateTableItems"
                        ></edit-cost-code>
                    </template>

                    <template v-if="isSuperAdmin" v-slot:rowActions="{ item }">
                        <table-actions
                            :item="item"
                            :api="api"
                            :update-table-items="updateTableItems"
                        ></table-actions>
                    </template>
                </d-table>
            </template>
        </data-iterator>
    </table-container>
</template>

<script>
import RestApiType from '@/api/RestApiType'
import RestApiCollection from '@/api/RestApiCollection'
import DataIteratorMixin from '@/components/mixins/DataIteratorMixin'
import DataTableMixin from '@/components/mixins/DataTableMixin'
import routeType from '@/router/routeType'
import IdFromIri from '@/services/IdFromIri'
import EditInList from '@/components/mixins/EditInList'
import Status from '@/components/elements/Status'
import Warning from '@/components/elements/Warning'
import DeliveryStatus from '@/components/elements/DeliveryStatus'
import Flag from '@/components/elements/Flag'
import DropOff from '@/components/elements/DropOff'
import OrderEvent from '@/pages/orders/mixins/OrderEvent'
import Headers from '@/pages/orders/mixins/Headers'
import ListFilters from '@/pages/orders/mixins/ListFilters'
import AuthMixin from '@/components/mixins/AuthMixin'
import TableActions from '@/components/pages/orders/TableActions'
import Identifier from '@/components/pages/orders/Identifier'
import EditCostCode from '@/components/pages/orders/EditCostCode'

export default {
    name: 'Table',
    components: {
        EditCostCode,
        Identifier,
        TableActions,
        DropOff,
        Flag,
        DeliveryStatus,
        Status,
        Warning,
    },
    mixins: [
        DataIteratorMixin,
        DataTableMixin,
        EditInList,
        OrderEvent,
        Headers,
        ListFilters,
        AuthMixin,
    ],
    data() {
        return {
            api: RestApiType.ORDERS,
            routeType: routeType,
            filters: {
                order: {
                    createdAt: 'desc',
                },
            },
        }
    },
    computed: {
        tableName() {
            return this.$t('labels.orders')
        },
    },
    methods: {
        getIdFromIri(item, key, rt, paramKey) {
            if (!item) return {}
            const id = IdFromIri.getId(item[key])
            let params = {}
            params[paramKey] = id

            return id
                ? {
                      name: rt,
                      params: params,
                  }
                : {}
        },
        removeWarning(itemId) {
            RestApiCollection.get(this.api).removeDeliveryWarning(itemId)
        },
    },
}
</script>
